/**
 * @file Arbres geants du hub (on passe dessous) : objets DEDIES non batches (au plus ~12) pour pouvoir rendre la couronne translucide par instance.
 *       Tronc = maillage opaque ; couronne = maillage a materiau clone (opacite) + contour a opacite variable. Quand un chat est sous la couronne
 *       ou masque par elle a l'ecran, la couronne passe en fondu a ~0.35 puis revient a 1 (lissage exponentiel : pas de scintillement).
 */
import { BufferGeometry, Group, Mesh, MeshToonMaterial } from 'three';
import type { ShaderMaterial, Scene } from 'three';
import { MergeBuilder } from '../factories/MergeBuilder';
import type { TargetFactory, TargetProto } from '../factories/TargetFactory';
import type { ResourceTracker } from '../gpu/ResourceTracker';
import type { ToonKit } from '../toon/toon';
import type { TargetFrame } from './InstancedTargets';

/** Geometrie vide pour les meshes sans piece (tronc ou couronne absents). */
const EMPTY = new BufferGeometry();

/** Opacite de la couronne quand un chat est dessous / derriere, et vitesse de fondu (1/s). */
const COVERED_OPACITY = 0.35;
const FADE_RATE = 7;
/** Part du rayon d'ombre en dessous de laquelle un chat est « a l'ombre » (et partie du rayon utilisee pour le masquage a l'ecran). */
const UNDER_FRACTION = 0.95;
const SCREEN_FRACTION = 0.9;
/** Hauteur (u) du centre d'un chat pour le test de masquage a l'ecran. */
const CAT_CENTER_HEIGHT = 0.9;

interface GiantGeo {
  trunk: BufferGeometry | null;
  canopy: BufferGeometry | null;
}

interface Giant {
  group: Group;
  trunk: Mesh;
  canopy: Mesh;
  canopyHull: Mesh;
  canopyMat: MeshToonMaterial;
  hullMat: ShaderMaterial;
  opacity: number;
  stamp: number;
  key: string;
  // etat de la frame
  id: number;
  x: number;
  z: number;
  /** Rayon de la couronne et hauteur de son centre (monde). */
  radius: number;
  mid: number;
}

export class GiantTrees {
  private readonly geos = new Map<string, GiantGeo>();
  private readonly live = new Map<number, Giant>();
  private readonly pool: Giant[] = [];
  private stamp = 0;
  private readonly rootMat: MeshToonMaterial;

  constructor(
    private readonly scene: Scene,
    private readonly factory: TargetFactory,
    private readonly kit: ToonKit,
    private readonly res: ResourceTracker,
  ) {
    this.rootMat = kit.toon('#ffffff', { vertexColors: true });
  }

  begin(): void {
    this.stamp++;
  }

  /** Met a jour (ou cree) le geant de l'entite : position monde (x, z = -y), lacet, echelle. `canopyRadius` = rayon de la couronne (monde). */
  draw(f: TargetFrame, canopyRadius: number): void {
    const proto = this.factory.proto(f.def, f.variant);
    const key = proto.key;
    let g = this.live.get(f.id);
    if (g && g.key !== key) {
      this.release(f.id, g);
      g = undefined;
    }
    if (!g) {
      g = this.acquire(proto);
      this.live.set(f.id, g);
    }
    g.stamp = this.stamp;
    g.id = f.id;
    g.x = f.x;
    g.z = -f.y;
    g.radius = canopyRadius;
    g.mid = proto.canopyMid ?? proto.height * 0.7;
    g.group.position.set(f.x, 0, -f.y);
    g.group.rotation.y = f.rot;
    g.group.scale.setScalar(f.scale);
  }

  /**
   * Fin de frame : retire les geants disparus, puis calcule le fondu des couronnes. `cats` = [x0, z0, x1, z1...] (monde Three, `n` chats),
   * (shiftX, shiftZ) = decalage au sol par unite de hauteur de la ligne de visee (un point a la hauteur h se projette en p + h * shift),
   * `sinPitch` = sinus de l'inclinaison de la camera (ecrasement en profondeur du disque de la couronne a l'ecran).
   */
  end(dt: number, cats: Float32Array, n: number, shiftX: number, shiftZ: number, sinPitch: number): void {
    for (const [id, g] of this.live) {
      if (g.stamp !== this.stamp) this.release(id, g);
    }
    const k = 1 - Math.exp(-FADE_RATE * Math.max(0, dt));
    const sh = Math.hypot(shiftX, shiftZ);
    const ax = sh > 1e-6 ? shiftX / sh : 0;
    const az = sh > 1e-6 ? shiftZ / sh : 1;
    for (const g of this.live.values()) {
      let covered = false;
      const R = g.radius;
      const cx = g.x + shiftX * g.mid;
      const cz = g.z + shiftZ * g.mid;
      for (let i = 0; i < n && !covered; i++) {
        const px = cats[i * 2] ?? 0;
        const pz = cats[i * 2 + 1] ?? 0;
        if (Math.hypot(px - g.x, pz - g.z) < R * UNDER_FRACTION) covered = true;
        else {
          // le chat (centre a CAT_CENTER_HEIGHT) se projette dans l'ellipse de la couronne a l'ecran : lateral x R, profondeur x R / sin(pitch)
          const dx = px + shiftX * CAT_CENTER_HEIGHT - cx;
          const dz = pz + shiftZ * CAT_CENTER_HEIGHT - cz;
          const along = (dx * ax + dz * az) * sinPitch;
          const side = dx * az - dz * ax;
          const r = R * SCREEN_FRACTION;
          if ((along * along + side * side) / (r * r) < 1) covered = true;
        }
      }
      const target = covered ? COVERED_OPACITY : 1;
      g.opacity += (target - g.opacity) * k;
      if (Math.abs(g.opacity - target) < 0.005) g.opacity = target;
      this.applyOpacity(g);
    }
  }

  /** Vrai si le point (x, z) monde est sous la couronne d'un geant (ombre) : teinte « a l'ombre » des chats. */
  isShaded(x: number, z: number): boolean {
    for (const g of this.live.values()) if (Math.hypot(x - g.x, z - g.z) < g.radius * UNDER_FRACTION) return true;
    return false;
  }

  dispose(): void {
    for (const g of [...this.live.values(), ...this.pool]) g.group.removeFromParent();
    this.live.clear();
    this.pool.length = 0;
    this.geos.clear();
  }

  // ------------------------------------------------------------------------------------------------------------------

  private applyOpacity(g: Giant): void {
    const o = g.opacity;
    const solid = o >= 0.999;
    g.canopyMat.opacity = o;
    g.canopyMat.transparent = !solid;
    g.canopyMat.depthWrite = solid;
    const u = g.hullMat.uniforms['uOpacity'];
    if (u) u.value = o;
    g.hullMat.transparent = !solid;
    g.hullMat.depthWrite = solid;
  }

  private geometryOf(proto: TargetProto): GiantGeo {
    let out = this.geos.get(proto.key);
    if (out) return out;
    const trunk = new MergeBuilder();
    const canopy = new MergeBuilder();
    for (const part of proto.parts) (part.canopy ? canopy : trunk).addRaw(part.geometry, part.color);
    const build = (b: MergeBuilder): BufferGeometry | null => {
      const geometry = b.fill('main');
      return geometry ? this.res.track(geometry) : null;
    };
    out = { trunk: build(trunk), canopy: build(canopy) };
    this.geos.set(proto.key, out);
    return out;
  }

  private acquire(proto: TargetProto): Giant {
    const geo = this.geometryOf(proto);
    let g = this.pool.pop();
    if (!g) {
      const group = new Group();
      const trunk = new Mesh(undefined, this.rootMat);
      const canopyMat = this.res.track(new MeshToonMaterial({ gradientMap: this.kit.gradient, vertexColors: true }));
      const canopy = new Mesh(undefined, canopyMat);
      const hullMat = this.kit.fadeOutline();
      const canopyHull = new Mesh(undefined, hullMat);
      canopyHull.renderOrder = -1;
      canopyHull.name = 'outline';
      group.add(trunk, canopy, canopyHull);
      g = { group, trunk, canopy, canopyHull, canopyMat, hullMat, opacity: 1, stamp: 0, key: '', id: -1, x: 0, z: 0, radius: 1, mid: 1 };
    }
    // maillages : tronc (+ contour enfant), couronne (+ coque a opacite variable)
    for (const child of [...g.trunk.children]) child.removeFromParent();
    g.trunk.geometry = geo.trunk ?? EMPTY;
    g.trunk.visible = geo.trunk !== null;
    if (geo.trunk) g.trunk.add(this.kit.outlineFor(geo.trunk));
    g.canopy.geometry = geo.canopy ?? EMPTY;
    g.canopy.visible = geo.canopy !== null;
    g.canopyHull.geometry = geo.canopy ? this.kit.hullGeometry(geo.canopy) : EMPTY;
    g.canopyHull.visible = geo.canopy !== null;
    g.key = proto.key;
    g.opacity = 1;
    this.applyOpacity(g);
    this.scene.add(g.group);
    return g;
  }

  private release(id: number, g: Giant): void {
    this.live.delete(id);
    g.group.removeFromParent();
    this.pool.push(g);
  }
}

